import React, { useState } from "react";
import { Loader2, Check, ChevronRight, MapPin, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import MapLocationPicker, { GeocodedLocationData } from "./MapLocationPicker";
import { AddPatientAddressPayload, PatientAddress } from "./types";
import { AddressService } from "./address.service";

interface ManualAddressFormProps {
  patientId: string;
  onAddressCreated: (address: PatientAddress) => void;
  onCancel: () => void;
}

export const ManualAddressForm: React.FC<ManualAddressFormProps> = ({
  patientId,
  onAddressCreated,
  onCancel,
}) => {
  const [step, setStep] = useState<"fields" | "map">("fields");

  // Form Fields
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [addressName, setAddressName] = useState("Flat");
  const [landmark, setLandmark] = useState("");

  // Map & Pin location
  const [locationData, setLocationData] = useState<GeocodedLocationData | null>(null);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleNextToMap = () => {
    if (!address.trim()) {
      toast.error("Address is required");
      return;
    }
    if (!pincode.trim()) {
      toast.error("Pincode is required");
      return;
    }

    // Geocode full string to get initial map center
    const fullSearch = `${address}, ${city}, ${state} ${pincode}`.trim();
    if (window.google && window.google.maps) {
      setGeocodingAddress(true);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: fullSearch }, (results, status) => {
        setGeocodingAddress(false);
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          setLocationData({
            lat: loc.lat(),
            lng: loc.lng(),
            address: address.trim(),
            city: city.trim(),
            state: state.trim(),
            pincode: pincode.trim(),
          });
        } else {
          // Default location fallback
          setLocationData({
            lat: 26.8467,
            lng: 80.9462,
            address: address.trim(),
            city: city.trim(),
            state: state.trim(),
            pincode: pincode.trim(),
          });
        }
        setStep("map");
      });
    } else {
      setLocationData({
        lat: 26.8467,
        lng: 80.9462,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        pincode: pincode.trim(),
      });
      setStep("map");
    }
  };

  const handleLocationSelect = (data: GeocodedLocationData) => {
    setLocationData(data);
    if (data.address && !address) setAddress(data.address);
    if (data.city && !city) setCity(data.city);
    if (data.state && !state) setState(data.state);
    if (data.pincode && !pincode) setPincode(data.pincode);
  };

  const handleSave = async () => {
    if (!locationData) {
      toast.error("Please confirm pin location on map");
      return;
    }

    const payload: AddPatientAddressPayload = {
      patientId,
      address: address.trim(),
      city: city.trim() || "Lucknow",
      state: state.trim() || "Uttar Pradesh",
      pincode: pincode.trim(),
      latitude: locationData.lat,
      longitude: locationData.lng,
      isDefault: false,
      addressName: addressName.trim() || "Flat",
      landmark: landmark.trim(),
    };

    try {
      setSaving(true);
      const res = await AddressService.addPatientAddress(payload);
      if (res.success && res.data) {
        toast.success("Address saved successfully!");
        onAddressCreated(res.data);
      } else {
        const newAddress: PatientAddress = {
          id: res.data?.id || Date.now(),
          patientId,
          address: payload.address,
          city: payload.city,
          state: payload.state,
          pincode: payload.pincode,
          latitude: payload.latitude,
          longitude: payload.longitude,
          addressName: payload.addressName,
          landmark: payload.landmark,
        };
        toast.success(res.message || "Address saved!");
        onAddressCreated(newAddress);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3.5">
      {step === "fields" ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">
                Full Address / Street <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="House no, Street name, Area"
                required
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Lucknow"
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="e.g. Uttar Pradesh"
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">
                Pincode <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                placeholder="6-digit Pincode"
                required
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
              />
            </div>

            <div>
              <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">Address Tag</label>
              <select
                value={addressName}
                onChange={(e) => setAddressName(e.target.value)}
                className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800 bg-white"
              >
                <option value="Home">Home</option>
                <option value="Work">Work</option>
                <option value="Flat">Flat / Apartment</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleNextToMap}
              disabled={geocodingAddress || !address.trim() || !pincode.trim()}
              className="px-4 py-2 text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-xs"
            >
              {geocodingAddress ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Locating...
                </>
              ) : (
                <>
                  Next: Pin Location <ChevronRight size={14} />
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep("fields")}
              className="text-xs font-bold text-slate-600 hover:text-slate-800 flex items-center gap-1 cursor-pointer"
            >
              <ArrowLeft size={14} /> Back to Address Form
            </button>
            <span className="text-[0.7rem] font-bold text-slate-400 uppercase tracking-wide">
              Step 2 of 2: Adjust Pin Location
            </span>
          </div>

          <MapLocationPicker
            initialCenter={locationData ? { lat: locationData.lat, lng: locationData.lng } : null}
            onLocationSelect={handleLocationSelect}
          />

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs flex flex-col gap-1 text-slate-700">
            <div>
              <span className="text-slate-400 font-semibold">Address:</span>{" "}
              <strong>{address}</strong>
            </div>
            <div>
              <span className="text-slate-400 font-semibold">Coordinates:</span>{" "}
              <span>{locationData?.lat.toFixed(6)}, {locationData?.lng.toFixed(6)}</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-xs"
            >
              {saving ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Saving Address...
                </>
              ) : (
                <>
                  <Check size={14} /> Save & Select Address
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualAddressForm;
